import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { Input } from '@shared/ui/Input';
import { Button } from '@shared/ui/Button';
import { SocialButton } from '@shared/ui/SocialButton';
import { useAuth } from '../model/useAuth';
import { useNavigate } from '@tanstack/react-router';

const REMEMBER_EMAIL_KEY = 'devlint_remember_email';

interface LoginFormProps {
  onToggleMode?: () => void;
  showToggle?: boolean;
}

/**
 * Login Form Component - Fully Responsive
 * Handles: Email/password login, OAuth providers, Remember Me
 */
export function LoginForm({ onToggleMode, showToggle = true }: LoginFormProps) {
  const { t } = useTranslation();
  const { login, loginWithProvider } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  
  const [formData, setFormData] = useState(() => {
    const savedEmail = localStorage.getItem(REMEMBER_EMAIL_KEY) || '';
    return {
      email: savedEmail,
      password: '',
      rememberMe: savedEmail.length > 0,
    };
  });

  const validateForm = () => {
    const newErrors: { email?: string; password?: string } = {};
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = t('auth.errors.emailRequired', 'Email is required');
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = t('auth.errors.emailInvalid', 'Invalid email format');
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = t('auth.errors.passwordRequired', 'Password is required');
    } else if (formData.password.length < 8) {
      newErrors.password = t('auth.errors.passwordMinLength', 'Password must be at least 8 characters');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);

    try {
      await login(formData.email, formData.password);

      // Save or clear remembered email
      if (formData.rememberMe) {
        localStorage.setItem(REMEMBER_EMAIL_KEY, formData.email);
      } else {
        localStorage.removeItem(REMEMBER_EMAIL_KEY);
      }

      navigate({ to: '/dashboard' });
    } catch (error) {
      console.error('Login failed:', error);
      setErrors({
        password: t('auth.errors.invalidCredentials', 'Invalid email or password'),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSocialAuth = async (provider: 'google' | 'github') => {
    try {
      await loginWithProvider(provider);
    } catch (error) {
      console.error(`${provider} login failed:`, error);
      // TODO: Show error message to user
    }
  };

  return (
    <div className="w-full space-y-6">
      {/* Title - Responsive Typography */}
      <div className="space-y-2">
        <h2 className="text-2xl sm:text-3xl font-bold text-text-primary font-mono tracking-tight">
          {t('auth.welcomeBack')}
        </h2>
        <p className="text-sm sm:text-base text-text-secondary">
          {t('auth.loginSubtitle')}
        </p>
      </div>

      {/* Social Login Buttons - Full Width */}
      <div className="w-full space-y-3">
        <SocialButton
          provider="google"
          onClick={() => handleSocialAuth('google')}
          type="button"
          className="w-full"
        >
          {t('auth.googleLogin')}
        </SocialButton>
        <SocialButton
          provider="github"
          onClick={() => handleSocialAuth('github')}
          type="button"
          className="w-full"
        >
          {t('auth.githubLogin')}
        </SocialButton>
      </div>

      {/* Divider */}
      <div className="relative w-full">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full divider" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-surface px-3 sm:px-4 text-text-muted font-mono">
            {t('auth.orContinueWith')}
          </span>
        </div>
      </div>

      {/* Form - Responsive Spacing */}
      <form onSubmit={handleSubmit} className="w-full space-y-4 sm:space-y-5">
        <Input
          id="email"
          type="email"
          label={t('auth.email')}
          placeholder={t('auth.emailPlaceholder')}
          value={formData.email}
          onChange={(e) => {
            setFormData({ ...formData, email: e.target.value });
            if (errors.email) setErrors({ ...errors, email: undefined });
          }}
          icon={<Mail className="h-4 w-4" />}
          error={errors.email}
          corners="asymmetric"
          className="w-full"
        />

        <Input
          id="password"
          type={showPassword ? 'text' : 'password'}
          label={t('auth.password')}
          placeholder={t('auth.passwordPlaceholder')}
          value={formData.password}
          onChange={(e) => {
            setFormData({ ...formData, password: e.target.value });
            if (errors.password) setErrors({ ...errors, password: undefined });
          }}
          icon={<Lock className="h-4 w-4" />}
          rightElement={
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="hover:text-primary transition-colors focus:outline-none"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          }
          error={errors.password}
          corners="asymmetric"
          className="w-full"
        />

        {/* Remember Me / Forgot Password - Responsive Layout */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 text-sm">
          <label className="flex items-center gap-2 text-text-secondary cursor-pointer min-h-[44px] sm:min-h-0">
            <input
              type="checkbox"
              checked={formData.rememberMe}
              onChange={(e) => setFormData({ ...formData, rememberMe: e.target.checked })}
              className="rounded border-border bg-surface text-primary focus:ring-primary focus:ring-offset-background w-4 h-4"
            />
            <span className="font-mono">{t('auth.rememberMe')}</span>
          </label>
          <a
            href="#"
            className="text-primary hover:text-primary-hover font-mono transition-colors min-h-[44px] sm:min-h-0 flex items-center"
          >
            {t('auth.forgotPassword')}
          </a>
        </div>

        {/* Submit Button - Full Width */}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          corners="asymmetric"
          loading={loading}
          className="w-full mt-6"
        >
          {t('auth.loginButton')}
        </Button>
      </form>

      {/* Toggle to Register - Responsive Text */}
      {showToggle && onToggleMode && (
        <div className="text-center text-sm">
          <span className="text-text-secondary font-mono">
            {t('auth.noAccount')}{' '}
          </span>
          <button
            onClick={onToggleMode}
            type="button"
            className="text-primary hover:text-primary-hover font-mono font-semibold transition-colors min-h-[44px] inline-flex items-center"
          >
            {t('auth.signUpLink')}
          </button>
        </div>
      )}
    </div>
  );
}
